import type { ProductCard as ProductCardData } from "@ellaandil/formatters";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";
import { Badge } from "@/components/ui/Badge";
import { Price } from "@/components/ui/Price";
import { ProductCardImage } from "@/components/ui/ProductCardImage";
import { Skeleton } from "@/components/ui/Skeleton";
import { Link as NextLink, productCanonicalPath } from "@/i18n/routing";
import { getProductCardProps } from "@/lib/depict/tracking";

/**
 * Optional Depict tracking IDs that callers (e.g. the PLP/search sources) may
 * attach to a product card. When present they're rendered as data attributes
 * on the card's root element so DPC's `auto_observe` can attribute impressions
 * and clicks to the originating Depict response.
 */
type DepictTrackingFields = Partial<{
	depictListingResultId: string;
	depictSearchResultId: string;
	depictProductId: string;
}>;

const cardVariants = cva("flex flex-col items-start relative", {
	variants: {
		size: {
			small: "gap-grid-component-spacing-2xs",
			medium: "gap-grid-component-spacing-2xs",
			large: "gap-4",
		},
	},
	defaultVariants: {
		size: "large",
	},
});

type ProductCardProps = VariantProps<typeof cardVariants> & {
	product: ProductCardData & DepictTrackingFields;
	className?: string;
	darkMode?: boolean;
	/** Default: visible on `medium`/`large`. Pass `false` to suppress (e.g. PLP). */
	showBrand?: boolean;
	/**
	 * Tells `next/image` to load this card's image eagerly + with high fetch
	 * priority. Set on the first few cards in a grid (typically in the LCP
	 * region) so they paint as early as possible. Default: false.
	 */
	priority?: boolean;
};

const MAX_VISIBLE_SWATCHES = 5;

export const ProductCard = forwardRef<HTMLAnchorElement, ProductCardProps>(
	({ product, size = "large", className, darkMode, showBrand: showBrandProp, priority }, ref) => {
		const firstImage = product.media?.[0];
		const secondImage = product.media?.[1];
		const hasBadges = product.showAsOnSale || product.showAsNew;
		const showBrand = showBrandProp ?? size !== "small";
		const colorSwatches = (() => {
			const swatches: { hex: string }[] = [];
			const seen = new Set<string>();
			const add = (hex: string | undefined) => {
				if (!hex || seen.has(hex)) return;
				seen.add(hex);
				swatches.push({ hex });
			};
			add(product.swatch?.hex);
			for (const v of product.variants ?? []) add(v.swatch?.hex);
			if (swatches.length === 0) {
				for (const c of product.colorGroup ?? []) add(c.hex);
			}
			return swatches;
		})();
		const showColors = colorSwatches.length > 0;

		const trackingAttrs = getProductCardProps({
			productId: product.depictProductId,
			listingId: product.depictListingResultId,
			searchResultId: product.depictSearchResultId,
		});

		return (
			<NextLink
				ref={ref}
				href={productCanonicalPath(product.uri)}
				className={twMerge(cardVariants({ size }), className)}
				{...trackingAttrs}
			>
				{/* Image */}
				<ProductCardImage
					firstImage={firstImage}
					secondImage={secondImage}
					productName={product.name}
					size={size || "large"}
					priority={priority}
				>
					{hasBadges && (
						<div
							className={twMerge(
								"absolute top-0 left-0 right-0 flex gap-grid-component-spacing-4xs items-start",
								size === "small" ? "p-grid-component-spacing-3xs" : "p-grid-component-spacing-2xs",
							)}
						>
							{product.showAsOnSale && (
								<Badge color="primaryLight" size="small">
									Sale
								</Badge>
							)}
							{product.showAsNew && (
								<Badge color="sand" size="small">
									New
								</Badge>
							)}
						</div>
					)}
				</ProductCardImage>

				{/* Details */}
				<div className="flex flex-col gap-grid-component-spacing-2xs items-start w-full shrink-0">
					{/* Text block */}
					<div
						className={twMerge(
							"flex flex-col items-start w-full",
							size === "small"
								? "gap-grid-component-spacing-none"
								: "gap-grid-component-spacing-5xs",
						)}
					>
						{showBrand && product.brandName && (
							<span className="font-body font-regular text-xs leading-[1.5] text-text-dark-text-2 w-full">
								{product.brandName}
							</span>
						)}
						<span
							className={twMerge(
								"font-display font-light leading-[1.25] text-text-dark-text-1 w-full",
								size === "large" ? "text-base" : "text-xs",
								size === "small" && "overflow-hidden text-ellipsis",
								darkMode && "text-text-light-text-1",
							)}
						>
							{product.name}
						</span>
					</div>

					{/* Color Swatches */}
					{showColors && (
						<div className="flex gap-grid-component-spacing-2xs items-center shrink-0">
							{colorSwatches.slice(0, MAX_VISIBLE_SWATCHES).map((swatch) => (
								<span
									key={swatch.hex}
									className="size-1.5 shrink-0 rounded-full border border-black/10"
									style={{ backgroundColor: swatch.hex }}
								/>
							))}
							{colorSwatches.length > MAX_VISIBLE_SWATCHES && (
								<span className="font-body font-regular text-xs leading-[1.15] text-text-dark-text-2 uppercase tabular-nums lining-nums whitespace-nowrap">
									+{colorSwatches.length - MAX_VISIBLE_SWATCHES}
								</span>
							)}
						</div>
					)}

					{/* Price */}
					<Price
						variant="horizontal"
						price={product.price ?? ""}
						originalPrice={product.showAsOnSale ? product.priceBeforeDiscount : undefined}
						darkMode={darkMode}
					/>
				</div>
			</NextLink>
		);
	},
);

export const ProductCardSkeleton = ({
	size = "large",
}: {
	size?: "small" | "medium" | "large";
}) => {
	const showBrand = size !== "small";
	const showColors = true;

	return (
		<div className={cardVariants({ size })}>
			{/* Image Skeleton */}
			<div className="relative aspect-[3/4] w-full overflow-hidden">
				<Skeleton className="h-full w-full" />
			</div>

			{/* Details Skeleton */}
			<div className="flex flex-col gap-grid-component-spacing-2xs items-start w-full shrink-0">
				{/* Text block */}
				<div
					className={twMerge(
						"flex flex-col items-start w-full",
						size === "small" ? "gap-grid-component-spacing-none" : "gap-grid-component-spacing-5xs",
					)}
				>
					{showBrand && (
						<Skeleton fitContent>
							<span className="font-body font-regular text-xs leading-[1.5] text-text-dark-text-2">
								Brand Name
							</span>
						</Skeleton>
					)}
					<Skeleton fitContent>
						<span
							className={twMerge(
								"font-display font-light leading-[1.25] text-text-dark-text-1",
								size === "large" ? "text-base" : "text-xs",
							)}
						>
							Product Name Here
						</span>
					</Skeleton>
				</div>

				{/* Color Swatches Skeleton */}
				{showColors && (
					<div className="flex gap-grid-component-spacing-2xs items-center shrink-0">
						{Array.from({ length: 3 }).map((_, i) => (
							<Skeleton key={i} fitContent>
								<span className="size-1.5 shrink-0 rounded-full bg-gray-300" />
							</Skeleton>
						))}
					</div>
				)}

				{/* Price Skeleton */}
				<Skeleton fitContent>
					<span className="font-body font-regular text-xs leading-[1.15] text-text-dark-text-2">
						$00.00
					</span>
				</Skeleton>
			</div>
		</div>
	);
};

ProductCard.displayName = "ProductCard";
